import { useContext, useMemo } from "react"

import { CurrentUserContext } from "context/CurrentUserContext"

import { ROLES } from "constants/enums"
import ROUTES from "constants/routes"
import FEATURE from "constants/featureFlags"

import { ISidebarItem } from "./ISidebar"

type MenuEntry = ISidebarItem | { divider: true }

const LOCATION_MANAGER_MENU_ITEMS: MenuEntry[] = [
    {
        itemKey: "dashboard",
        label: "menuItems.dashboard",
        to: ROUTES.HOME,
        icon: "dashboard",
    },
    {
        itemKey: "reports",
        label: "menuItems.reports",
        to: `${ROUTES.REPORTS}${ROUTES.DAILY}`,
        icon: "reports",
        feature: FEATURE.REPORTS,
        subitems: [
            {
                itemKey: "daily-reports",
                label: "menuItems.dailyReport",
                to: `${ROUTES.REPORTS}${ROUTES.DAILY}`,
                icon: "box",
            },
            {
                itemKey: "weekly-reports",
                label: "menuItems.weeklyReport",
                to: `${ROUTES.REPORTS}${ROUTES.WEEKLY}`,
                icon: "box",
            },
            {
                itemKey: "monthly-reports",
                label: "menuItems.monthlyReport",
                to: `${ROUTES.REPORTS}${ROUTES.MONTHLY}`,
                icon: "box",
            },
            {
                itemKey: "periodical-reports",
                label: "menuItems.periodicalReport",
                to: `${ROUTES.REPORTS}${ROUTES.PERIODICAL}`,
                icon: "box",
            },
            {
                itemKey: "yearly-reports",
                label: "menuItems.yearlyReport",
                to: `${ROUTES.REPORTS}${ROUTES.YEARLY}`,
                icon: "box",
            },
            {
                itemKey: "entry-analysis",
                label: "menuItems.entryAnalysis",
                to: `${ROUTES.REPORTS}${ROUTES.ENTRY_ANALYSIS}`,
                icon: "box",
            },
        ],
    },
    {
        divider: true,
    },
    {
        itemKey: "peak-traffic-prediction",
        label: "menuItems.peakTrafficPrediction",
        to: `${ROUTES.REPORTS}${ROUTES.PEAK_TRAFFIC_PREDICTION}`,
        icon: "chart-line",
        feature: FEATURE.VISIT_PREDICTION,
    },
    {
        itemKey: "performance-comparison",
        label: "menuItems.performanceComparison",
        to: `${ROUTES.REPORTS}${ROUTES.PERFORMANCE_COMPARISON}`,
        icon: "performance-compare",
        feature: FEATURE.LOCATION_PERFORMANCE,
    },
]

const REGIONAL_MANAGER_MENU_ITEMS: MenuEntry[] = [
    ...LOCATION_MANAGER_MENU_ITEMS,
    {
        divider: true,
    },
    {
        itemKey: "compare-locations",
        label: "menuItems.compareLocations",
        to: ROUTES.COMPARE_LOCATIONS,
        icon: "compare",
        feature: FEATURE.LOCATION_COMPARISON,
    },
]

const COMPANY_MANAGER_MENU_ITEMS: MenuEntry[] = [
    ...REGIONAL_MANAGER_MENU_ITEMS,
    {
        divider: true,
    },
    {
        itemKey: "region-management",
        label: "menuItems.regionManagement",
        to: ROUTES.REGION_MANAGEMENT,
        icon: "location-route",
    },
    {
        itemKey: "location-management",
        label: "menuItems.locationManagement",
        to: ROUTES.LOCATION_MANAGEMENT,
        icon: "map-pin",
    },
    {
        itemKey: "working-time-management",
        label: "menuItems.workingTimeManagement",
        to: ROUTES.WORKING_TIME_MANAGEMENT,
        icon: "workingTime",
    },
    {
        itemKey: "mailing-report-management",
        label: "menuItems.mailingReports",
        to: ROUTES.MAILING_REPORTS,
        icon: "mailing-report",
        feature: FEATURE.MAILING_REPORTS,
    },
]

const ADMIN_MENU_ITEMS: MenuEntry[] = [
    ...COMPANY_MANAGER_MENU_ITEMS,
    {
        divider: true,
    },
    {
        itemKey: "company-management",
        label: "menuItems.companyManagement",
        to: ROUTES.COMPANY_MANAGEMENT,
        icon: "company",
    },
    {
        itemKey: "countries-management",
        label: "menuItems.countriesManagement",
        to: ROUTES.COUNTRY_MANAGEMENT,
        icon: "country",
    },
    {
        itemKey: "city-management",
        label: "menuItems.cityManagement",
        to: ROUTES.CITY_MANAGEMENT,
        icon: "city",
    },
    {
        itemKey: "gate-management",
        label: "menuItems.gateManagement",
        to: ROUTES.GATE_MANAGEMENT,
        icon: "gate",
    },
    {
        itemKey: "camera-management",
        label: "menuItems.cameraManagement",
        to: ROUTES.CAMERA_MANAGEMENT,
        icon: "camera",
    },
    {
        itemKey: "user-management",
        label: "menuItems.userManagement",
        to: ROUTES.USER_MANAGEMENT,
        icon: "people",
    },
    {
        itemKey: "translation-management",
        label: "menuItems.translationManagement",
        to: ROUTES.TRANSLATION_MANAGEMENT,
        icon: "translate",
    },
    {
        itemKey: "visit-management",
        label: "menuItems.visitManagement",
        to: ROUTES.VISIT_MANAGEMENT,
        icon: "visit",
    },
]

const DEFAULT_NEW_KEYS = new Set([
    "dashboard",
    "peak-traffic-prediction",
    "performance-comparison",
    "entry-analysis",
])

// Admin uses a separate dashboard screen that does not include the weekly
// insight, so the NOVO badge applies only to the two reports for admin.
const ADMIN_NEW_KEYS = new Set([
    "peak-traffic-prediction",
    "performance-comparison",
    "entry-analysis",
])

const applyIsNew = (
    items: MenuEntry[],
    newKeys: Set<string>
): MenuEntry[] =>
    items.map((item) => {
        if ("divider" in item) return item
        const subitems = item.subitems?.map((subitem) => ({
            ...subitem,
            isNew: newKeys.has(subitem.itemKey),
        }))
        return {
            ...item,
            subitems,
            isNew: newKeys.has(item.itemKey),
            hasNewSubitem: !!subitems?.some((subitem) => subitem.isNew),
        }
    })

const resolveMenuItems = (userRole): MenuEntry[] => {
    switch (userRole.id) {
        case ROLES.ROLE_ADMIN.id: {
            const adminMenuItems = applyIsNew(
                ADMIN_MENU_ITEMS,
                ADMIN_NEW_KEYS
            )
            const adminDashboard = adminMenuItems.findIndex(
                (item) => !("divider" in item) && item.itemKey === "dashboard"
            )
            const dashboardItem = adminMenuItems[adminDashboard] as ISidebarItem
            dashboardItem.to = ROUTES.ADMIN_DASHBOARD
            return adminMenuItems
        }
        case ROLES.ROLE_COMPANY_MANAGER.id:
            return applyIsNew(COMPANY_MANAGER_MENU_ITEMS, DEFAULT_NEW_KEYS)
        case ROLES.ROLE_REGIONAL_MANAGER.id:
            return applyIsNew(REGIONAL_MANAGER_MENU_ITEMS, DEFAULT_NEW_KEYS)
        case ROLES.ROLE_LOCATION_MANAGER.id:
            return applyIsNew(LOCATION_MANAGER_MENU_ITEMS, DEFAULT_NEW_KEYS)
        default:
            return []
    }
}

// Hides menu items whose feature flag is OFF for the user's company (ASA-225).
// Items without a feature (dashboard, always-on core management) are always
// kept. Dividers pass through.
const filterByFeature = (
    items: MenuEntry[],
    isFeatureEnabled: (key: string) => boolean
): MenuEntry[] =>
    items.filter((item) =>
        "divider" in item || !item.feature
            ? true
            : isFeatureEnabled(item.feature)
    )

// Removes redundant dividers left behind after feature filtering, so a section
// emptied by hidden items never renders as a double line (ASA-225): drops
// leading dividers, collapses consecutive dividers to one, and drops a trailing
// divider.
const collapseDividers = (items: MenuEntry[]): MenuEntry[] => {
    const result: MenuEntry[] = []
    items.forEach((item) => {
        if ("divider" in item) {
            const last = result[result.length - 1]
            if (!last || "divider" in last) return
        }
        result.push(item)
    })
    if (result.length && "divider" in result[result.length - 1]) {
        result.pop()
    }
    return result
}

const useMenuItems = (userRole) => {
    const { isFeatureEnabled, featuresLoaded } = useContext(CurrentUserContext)

    return useMemo(
        () =>
            userRole
                ? collapseDividers(
                      filterByFeature(
                          resolveMenuItems(userRole),
                          // Hide gated items until the feature map arrives
                          // instead of showing and then removing them.
                          (key) => featuresLoaded && isFeatureEnabled(key)
                      )
                  )
                : [],
        [userRole, isFeatureEnabled, featuresLoaded]
    )
}

export default useMenuItems
